/**
 * AdminUserQuickActionsModal — single modal that bundles the four most
 * common admin actions for a user:
 *
 *   1. Re-send activation email (fresh 24h token, hits the OLD one dead)
 *   2. Re-send password-reset email (fresh 1h token, hits the OLD one dead)
 *   3. Deactivate / Activate the user (toggles `is_active`)
 *   4. Delete the user (irreversible — confirmation required)
 *
 * Each action calls its own backend endpoint; errors surface inline so
 * the admin doesn't have to check network tab. After a destructive
 * action (delete), the parent should refresh its user list — that's the
 * `onChanged` callback.
 *
 * The component is purely presentational state-wise: it receives the
 * `user` to act on and two callbacks (`onClose`, `onChanged`). Any
 * confirmation dialogs are window.confirm — keep the modal stack flat.
 */
import { useState } from 'preact/hooks';
import {
  deleteUser,
  deactivateUser,
  activateUser,
  resendActivationEmail,
  resendPasswordResetEmail,
} from '../../services/adminService.js';


/**
 * @param {object} props
 * @param {object} props.user — the target user record (must include id, email, is_active, email_verified, admin_role)
 * @param {() => void} props.onClose
 * @param {() => void} props.onChanged — called after any state-changing action so the parent can refresh
 */
export function AdminUserQuickActionsModal({ user, onClose, onChanged }) {
  // Per-action transient state. `busy` is the action key currently running
  // ('delete', 'activate', 'resend-activation', 'resend-password-reset')
  // or null when idle. Lets us disable everything except the running button.
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  if (!user) return null;
  const isAdmin = !!user.admin_role;

  const run = async (key, fn, successMessage, { confirmText, requireConfirm = false } = {}) => {
    if (requireConfirm && !window.confirm(confirmText || 'Are you sure?')) return;
    setBusy(key);
    setError(null);
    setSuccess(null);
    try {
      await fn();
      setSuccess(successMessage);
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const handleResendActivation = () =>
    run(
      'resend-activation',
      () => resendActivationEmail(user.id),
      `Activation email sent to ${user.email}.`,
    );

  const handleResendPasswordReset = () =>
    run(
      'resend-password-reset',
      () => resendPasswordResetEmail(user.id),
      `Password-reset email sent to ${user.email}.`,
    );

  const handleToggleActive = () => {
    const action = user.is_active ? 'Deactivate' : 'Activate';
    run(
      'activate',
      () => (user.is_active ? deactivateUser(user.id) : activateUser(user.id)),
      `User ${action.toLowerCase()}d.`,
      {
        confirmText: `${action} ${user.email}?`,
        requireConfirm: true,
      },
    );
  };

  const handleDelete = () =>
    run(
      'delete',
      () => deleteUser(user.id),
      `User ${user.email} deleted.`,
      {
        confirmText:
          `Delete ${user.email}? This cancels their Stripe subscription ` +
          `and removes the account permanently. This action cannot be undone.`,
        requireConfirm: true,
      },
    );

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal admin-modal--glass admin-modal--narrow"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Quick actions for ${user.email}`}
      >
        <header className="admin-modal-header">
          <div>
            <h3 className="admin-modal-title">Quick actions</h3>
            <p className="admin-modal-sub">
              {user.email}{user.first_name || user.last_name
                ? ` · ${[user.first_name, user.last_name].filter(Boolean).join(' ')}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            className="admin-modal-close"
            onClick={onClose}
            aria-label="Close quick actions"
          >×</button>
        </header>

        {error && <div className="admin-banner admin-banner--error">{error}</div>}
        {success && <div className="admin-banner admin-banner--success">{success}</div>}

        <div className="admin-modal-body">
          <ActionRow
            title="Re-send activation email"
            description={
              user.email_verified
                ? 'User is already verified — disabled.'
                : 'Generates a fresh 24-hour token. Any earlier link is invalidated immediately.'
            }
            buttonLabel="Send"
            running={busy === 'resend-activation'}
            disabled={!!busy || user.email_verified}
            onClick={handleResendActivation}
          />
          <ActionRow
            title="Re-send password-reset email"
            description={
              !user.is_active
                ? 'User is inactive — reactivate first to send a usable reset link.'
                : 'Generates a fresh 1-hour token. Any earlier link is invalidated immediately.'
            }
            buttonLabel="Send"
            running={busy === 'resend-password-reset'}
            disabled={!!busy || !user.is_active}
            onClick={handleResendPasswordReset}
          />
          <ActionRow
            title={user.is_active ? 'Deactivate user' : 'Activate user'}
            description={
              isAdmin
                ? 'Cannot deactivate an admin from this dialog.'
                : user.is_active
                  ? 'User will be unable to sign in. Reversible.'
                  : 'Restore sign-in access.'
            }
            buttonLabel={user.is_active ? 'Deactivate' : 'Activate'}
            buttonVariant={user.is_active ? 'warning' : 'success'}
            running={busy === 'activate'}
            disabled={!!busy || isAdmin}
            onClick={handleToggleActive}
          />
          <ActionRow
            title="Delete user"
            description={
              isAdmin
                ? 'Cannot delete an admin from this dialog.'
                : 'Permanently removes the account and cancels any Stripe subscription. Irreversible.'
            }
            buttonLabel="Delete"
            buttonVariant="danger"
            running={busy === 'delete'}
            disabled={!!busy || isAdmin}
            onClick={handleDelete}
          />
        </div>
      </div>
    </div>
  );
}


function ActionRow({ title, description, buttonLabel, buttonVariant, running, disabled, onClick }) {
  const variantClass =
    buttonVariant === 'danger' ? 'admin-btn--danger'
      : buttonVariant === 'warning' ? 'admin-btn--warning'
        : buttonVariant === 'success' ? 'admin-btn--success'
          : 'admin-btn--secondary';
  return (
    <div className="admin-quick-action">
      <div className="admin-quick-action__text">
        <div className="admin-quick-action__title">{title}</div>
        <div className="admin-quick-action__desc">{description}</div>
      </div>
      <button
        type="button"
        className={`admin-btn ${variantClass}`}
        onClick={onClick}
        disabled={disabled}
      >
        {running ? 'Working…' : buttonLabel}
      </button>
    </div>
  );
}
